import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// sessionStorage key for the apply-context stashed before a redirect-based
// payment (MobilePay, Pay-by-Bank) leaves the page.
export const deltaReturnKey = (piId) => `deltaPayReturn-${piId}`;

/**
 * Top-level hook for the manage payment flows (pay / extend / edit).
 *
 * Redirect-based methods leave the page during confirmPayment and return with
 * ?payment_intent=...&redirect_status=.... This detects that return, rehydrates
 * the apply-context stashed before the redirect, and hands off to the flow's
 * `onFinalize` (which confirms server-side and applies the extension/edit/addons).
 *
 * Runs once on mount. The flow drives its own step state via the callbacks.
 *
 * @param {object}   opts
 * @param {function} opts.onFinalize  async (paymentIntentId, applyContext, { skipConfirm }) => void
 * @param {function} [opts.onProcessing]  called when a return is detected and finalize starts
 * @param {function} [opts.onError]       called with a message on failure
 */
export function useDeltaPaymentReturn({ onFinalize, onProcessing, onError }) {
  const [searchParams] = useSearchParams();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    const piId = searchParams.get('payment_intent');
    const redirectStatus = searchParams.get('redirect_status');
    if (!piId) return;
    handledRef.current = true;

    let applyContext = {};
    try {
      const stored = sessionStorage.getItem(deltaReturnKey(piId));
      if (stored) applyContext = JSON.parse(stored).applyContext || {};
    } catch (e) { /* sessionStorage unavailable — finalize with empty context */ }

    const clear = () => {
      try { sessionStorage.removeItem(deltaReturnKey(piId)); } catch (e) { /* ignore */ }
    };

    if (redirectStatus === 'failed') {
      clear();
      onError?.('Payment was not completed. Please try again.');
      return;
    }

    (async () => {
      onProcessing?.();
      try {
        // 'succeeded' or 'processing' → finalize. confirm-delta-payment is
        // idempotent, so a refresh after returning is safe.
        await onFinalize(piId, applyContext, { skipConfirm: false });
        clear();
      } catch (err) {
        onError?.(err?.message || 'Could not finalize your payment.');
      }
    })();
  }, []);
}
